import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BookData {
  title: string;
  author?: string;
  authors?: string[];
  open_library_id?: string;
  isbn13?: string;
  isbn10?: string;
}

export function useSaveToTBR(bookId: string, reviewId?: string, bookData?: BookData) {
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Only check if bookId is valid
    if (bookId && bookId.trim()) {
      checkIfSaved();
    }
  }, [bookId]);

  const checkIfSaved = async () => {
    // Guard against empty bookId
    if (!bookId || !bookId.trim()) {
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if book is already in user's TBR (user_books table)
      const { data, error } = await supabase
        .from('user_books')
        .select('id')
        .eq('user_id', user.id)
        .eq('book_id', bookId)
        .eq('shelf', 'to-read')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      setIsSaved(!!data);
    } catch (error) {
      console.error('Error checking if book is saved:', error);
    }
  };

  const toggleSave = async () => {
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Please sign in",
          description: "You need to be signed in to save books.",
          variant: "destructive",
        });
        return;
      }

      if (isSaved) {
        // Remove from TBR
        const { error } = await supabase
          .from('user_books')
          .delete()
          .eq('user_id', user.id)
          .eq('book_id', bookId)
          .eq('shelf', 'to-read');

        if (error) throw error;
        
        setIsSaved(false);
        toast({
          title: "Removed from TBR",
          description: "Book removed from your To Be Read list.",
        });
      } else {
        // Check if book exists in database first
        let finalBookId = bookId;
        
        if (!bookId || bookId === 'null' || bookId === 'undefined') {
          // Book doesn't exist yet, need to import it
          if (!bookData) {
            toast({
              title: "Cannot add book",
              description: "Book information is missing. Please try again.",
              variant: "destructive",
            });
            return;
          }

          // Search for the book using the search-books function
          const searchQuery = bookData.isbn13 || 
                            bookData.isbn10 ||
                            bookData.open_library_id || 
                            `${bookData.title} ${bookData.author || bookData.authors?.[0] || ''}`;

          const { data: searchResults, error: searchError } = await supabase.functions.invoke('search-books', {
            body: { query: searchQuery, limit: 1 }
          });

          if (searchError || !searchResults?.books?.[0]) {
            toast({
              title: "Book not found",
              description: "Could not find this book in our database. Please try searching for it manually.",
              variant: "destructive",
            });
            return;
          }

          finalBookId = searchResults.books[0].id;
          
          toast({
            title: "Book imported",
            description: "Book added to our database and your TBR!",
          });
        }

        // Add to TBR - privacy now controlled by profile privacy_mode
        const { error } = await supabase
          .from('user_books')
          .insert({
            user_id: user.id,
            book_id: finalBookId,
            shelf: 'to-read'
          });

        if (error) throw error;

        // Also track in book_saves table for analytics
        if (reviewId) {
          const { error: saveError } = await supabase
            .from('book_saves')
            .upsert({
              user_id: user.id,
              book_id: finalBookId,
              saved_from_review_id: reviewId
            }, {
              onConflict: 'user_id,book_id'
            });
          
          if (saveError) {
            console.warn('Could not track book save:', saveError);
          }
        }

        // Log activity for AI learning
        try {
          const { data: bookDataFetched } = await supabase
            .from('books')
            .select('title, cover_url')
            .eq('id', finalBookId)
            .single();

          if (bookDataFetched) {
            await supabase.from('activities').insert({
              user_id: user.id,
              type: 'book_saved',
              book_id: finalBookId,
              book_title: bookDataFetched.title,
              book_cover_url: bookDataFetched.cover_url,
              payload: {
                saved_from_review_id: reviewId,
                book_id: finalBookId
              }
            });
          }
        } catch (activityError) {
          console.warn('Could not log activity:', activityError);
        }
        
        setIsSaved(true);
        toast({
          title: "Added to TBR",
          description: "Book added to your To Be Read list.",
        });
      }
    } catch (error: any) {
      console.error('Error toggling save:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update save status.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    isSaved,
    toggleSave,
    loading
  };
}